import { CellContext } from '@tanstack/react-table';
import { Pencil } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { RequestsTableResponse } from 'src/api/services/RequestClient';
import { roles } from 'src/api/services/RoleClient';
import { RenderForAdmin } from 'src/components/Middleware';
import { Avatar, AvatarFallback, AvatarImage } from 'src/components/ui/avatar';
import { Button } from 'src/components/ui/button';
import { Link } from 'src/components/ui/link';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useToggle } from 'src/lib/state-utils';
import { str } from 'src/lib/utils';
import { appRoutes } from 'src/routes';
import { Can, permission } from 'src/services/Permission';
import { RequestEditorChangeDialog } from './request-editor-change-dialog';

type Props = {
  cell: CellContext<RequestsTableResponse, any>;
  companyId?: string;
};

type EditorAvatarNameProps = {
  fullName: string;
  avatarSrc?: string;
  showUnderlineOnHover?: boolean;
};

const EditorAvatarName = React.forwardRef<HTMLDivElement, EditorAvatarNameProps>(
  ({ fullName, avatarSrc, showUnderlineOnHover = false }, ref) => (
    <div ref={ref} className={'tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2'}>
      <Avatar size={'xs'} className={'tw-flex-shrink-0'}>
        <AvatarImage className={'tw-object-cover'} src={avatarSrc} alt={str.acronym(fullName)} />
        <AvatarFallback className={'tw-bg-orange tw-font-medium tw-text-white'}>
          {str.acronym(fullName)}
        </AvatarFallback>
      </Avatar>
      <span
        className={`tw-truncate tw-font-medium ${showUnderlineOnHover ? 'tw-underline-offset-2 group-hover:tw-underline' : ''}`}
      >
        {fullName}
      </span>
    </div>
  )
);

EditorAvatarName.displayName = 'EditorAvatarName';

const RequestEditorCell: React.FC<Props> = ({ cell, companyId }) => {
  const { t } = useTranslation('pages/shared');
  const user = cell.getValue();
  const [open, setOpen, toggle] = useToggle();

  return !user ? (
    <span>{t('common:unset')!}</span>
  ) : (
    <RenderForAdmin
      and={[roles.teamLead]}
      else={
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <EditorAvatarName
                fullName={cell.getValue().full_name}
                avatarSrc={
                  cell.getValue().avatar?.generated_conversions.square_200 ??
                  cell.getValue().avatar?.index_url
                }
              />
            </TooltipTrigger>
            <TooltipContent>{cell.getValue().full_name}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      }
    >
      <div className={'tw-flex tw-items-center'}>
        <Link
          to={generatePath(appRoutes.user, { userId: user.id })}
          variant={'unset'}
          underline={'unset'}
          className={'tw-group'}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <EditorAvatarName
                  fullName={cell.getValue().full_name}
                  avatarSrc={
                    cell.getValue().avatar?.generated_conversions.square_200 ??
                    cell.getValue().avatar?.index_url
                  }
                  showUnderlineOnHover
                />
              </TooltipTrigger>
              <TooltipContent>{cell.getValue().full_name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Link>
        <Can permission={permission.RequestsChangeEditor}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className={
                    'tw-text-neutral-600 tw-transition-colors group-hover/row:tw-visible md:tw-invisible'
                  }
                  variant={'ghost'}
                  size={'iconSm'}
                  onClick={toggle}
                >
                  <Pencil size={'16'} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('actions:edit_field', { defaultValue: 'Edit field' })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {open && (
            <RequestEditorChangeDialog
              companyId={cell.row.original.company?.id || companyId}
              requestId={cell.row.original.id}
              editorId={user.id}
              open={open}
              setOpen={setOpen}
              toggle={toggle}
              onSuccess={() => {
                cell.helpers.refetch();
              }}
            />
          )}
        </Can>
      </div>
    </RenderForAdmin>
  );
};

export default RequestEditorCell;
